import { LayoutDashboard, Bus, Ticket, Package, Eye } from 'lucide-react';

function isLight(color) {
  if (!color || typeof color !== 'string') return true;
  const m = color.match(/#([0-9a-fA-F]{6})/);
  if (!m) return true;
  const h = m[1];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 150;
}

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, active: true },
  { label: 'Viagens', icon: Bus },
  { label: 'Reservas', icon: Ticket },
  { label: 'Encomendas', icon: Package },
];

export default function BrandingPreview({
  nomeDoSistema = 'Sistema Tour',
  nomeEmpresa = 'Sua Agência',
  logo = '',
  corPrimaria = '#0147a0',
  corSecundaria = '#d72d21',
  corSidebar = '#001e45',
}) {
  const light = isLight(corSidebar);
  const fg = light ? '#0f172a' : '#f8fafc';
  const fgMuted = light ? '#64748b' : '#cbd5e1';
  const border = light ? 'rgba(15,23,42,0.1)' : 'rgba(255,255,255,0.12)';
  const activeBg = light ? `${corPrimaria}18` : 'rgba(255,255,255,0.14)';
  const activeFg = light ? corPrimaria : '#fff';

  const slugPreview = (nomeEmpresa || 'agencia').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Eye className="w-3.5 h-3.5 text-slate-400" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Preview ao vivo</p>
      </div>

      <div className="bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-200/60 border-b border-slate-200">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-2 text-[9px] text-slate-500 font-mono truncate">/{slugPreview}/dashboard</span>
        </div>

        {/* App frame */}
        <div className="flex" style={{ minHeight: 240 }}>
          {/* Sidebar */}
          <div className="w-28 flex-shrink-0 flex flex-col border-r"
            style={{ background: corSidebar, borderColor: border, color: fg }}>
            {/* Logo area */}
            <div className="px-2.5 py-2.5 border-b" style={{ borderColor: border }}>
              <div className="flex items-start gap-1.5">
                {logo ? (
                  <img src={logo} alt="" className="w-6 h-6 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-white font-bold text-[10px]"
                    style={{ background: corPrimaria }}>
                    {(nomeDoSistema || 'T')[0]}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-[8px] font-bold leading-tight truncate" style={{ color: fg }}>{nomeDoSistema || 'Sistema Tour'}</p>
                  <p className="text-[7px] font-bold tracking-widest uppercase" style={{ color: '#f9c900' }}>SISTEMA</p>
                </div>
              </div>
            </div>

            {/* Nav */}
            <div className="flex-1 px-1.5 py-2 space-y-0.5">
              {NAV.map(({ label, icon: Icon, active }) => (
                <div key={label}
                  className="flex items-center gap-1.5 px-1.5 py-1 rounded-md text-[8px] font-medium"
                  style={{ background: active ? activeBg : 'transparent', color: active ? activeFg : fgMuted }}>
                  <Icon className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">{label}</span>
                </div>
              ))}
            </div>

            {/* User */}
            <div className="px-2 py-1.5 border-t" style={{ borderColor: border }}>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[6px] font-bold flex-shrink-0"
                  style={{ background: corSecundaria }}>A</div>
                <p className="text-[7px] truncate" style={{ color: fgMuted }}>Admin</p>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 p-2.5 space-y-2 bg-[#F0F4FF]">
            <p className="text-[8px] font-bold text-slate-700">Bom dia, Admin!</p>

            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: 'Clientes', value: '142', accent: corPrimaria },
                { label: 'Viagens', value: '28', accent: corSecundaria },
                { label: 'Reservas', value: '95', accent: '#339933' },
                { label: 'Encomendas', value: '17', accent: '#c99a1a' },
              ].map(({ label, value, accent }) => (
                <div key={label} className="bg-white rounded border border-slate-200/80 overflow-hidden">
                  <div className="h-0.5" style={{ background: accent }} />
                  <div className="p-1.5">
                    <p className="text-[6px] font-bold text-slate-400 uppercase tracking-wide">{label}</p>
                    <p className="text-[10px] font-bold text-slate-800">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            <button type="button"
              className="w-full text-white text-[7px] font-bold py-1 rounded transition-opacity"
              style={{ background: corPrimaria }}>
              + Nova Reserva
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { label: 'Primária', color: corPrimaria },
          { label: 'Secundária', color: corSecundaria },
          { label: 'Sidebar', color: corSidebar },
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm border border-slate-200 flex-shrink-0" style={{ background: color }} />
            <span className="text-[10px] text-slate-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
